import { DisplayFormat, MappedEntity, SnakeMeta } from '@api';
import { ChartAPIReturn, OHLCPrice, SeriesType, TimeSeriesItem } from '@chart';
import { isPriceSnake, isYieldSnake } from '@helpers';
import { PriceDisplayReturn } from '@terminal/components/price-display-view/helpers/price-display.types';
import { isNonZeroReturn } from '@terminal/components/price-display-view/helpers/price-display-helpers';
import { LivePriceInfo } from '@terminal/hooks/use-live-price/use-live-price-store/useLivePriceStore.utils';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { priceDecimals } from '@terminal/utils/currency/currency';
import { getPriceChange } from '@terminal/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';
import React, { useEffect } from 'react';

import legendLivePriceStyles from '../legend-live-price/LegendLivePrice.module.scss';
import {
  LegendPriceIcons,
  PriceIcon,
} from '../legend-price-icons/LegendPriceIcons';

export interface LastClosePriceProps {
  iconMapping: PriceIcon[];
  price: PriceDisplayReturn;
  entity: MappedEntity;
  snakeMeta: SnakeMeta;
  legendColor: string;
  isPrimary: boolean;
  seriesType?: SeriesType;
  priceFormatter: (price: number) => string;
  ts: TimeSeriesItem[];
  hoverData?: {
    currentPrice: TimeSeriesItem;
    previousPrice?: TimeSeriesItem;
  };
  chartApiRef?: React.RefObject<ChartAPIReturn | null>;
  livePriceInfo?: LivePriceInfo;
}

export const LastClosePrice = ({
  entity,
  snakeMeta,
  legendColor,
  isPrimary,
  seriesType,
  priceFormatter,
  ts,
  iconMapping,
  price,
  hoverData,
  chartApiRef,
  livePriceInfo,
}: LastClosePriceProps) => {
  const isOHLCSeries =
    seriesType && ['bars', 'candlestick'].includes(seriesType);
  const isOHLCPrice = isOHLCSeries && isPrimary;
  const isLiveCandlesEnabled = useFlags(state =>
    state.getFeatureFlagState('live_candles_feature_flag')
  );

  const lastClosePrice =
    hoverData?.currentPrice?.close ?? ts[ts.length - 1].close;
  const beforeLastPrice =
    hoverData?.previousPrice?.close ?? ts[ts.length - 2]?.close;

  const decimals = entity.sub_class
    ? priceDecimals(entity.sub_class)(lastClosePrice)
    : undefined;

  const { priceChange } = getPriceChange({
    lastPrice: beforeLastPrice,
    newPrice: lastClosePrice ?? 0,
    decimals,
  });

  // Update chart with live price data
  useEffect(() => {
    if (
      chartApiRef?.current &&
      isPrimary &&
      !hoverData &&
      chartApiRef.current.getBaseData()?.resampleInterval === '1m' &&
      price.primary.isLive &&
      price.primary.price &&
      livePriceInfo &&
      isLiveCandlesEnabled
    ) {
      chartApiRef.current.updateLastPrice?.(
        livePriceInfo.price,
        livePriceInfo.time,
        entity.default_snake
      );
    }
  }, [
    livePriceInfo,
    chartApiRef,
    isPrimary,
    hoverData,
    price.primary.isLive,
    price.primary.price,
    seriesType,
    isLiveCandlesEnabled,
  ]);

  if (isOHLCPrice) {
    return (
      <OHLCPrice
        price={hoverData?.currentPrice ?? ts[ts.length - 1]}
        priceFormatter={priceFormatter}
      />
    );
  }

  const isPriceOrYield =
    isPriceSnake(snakeMeta.snake) || isYieldSnake(snakeMeta.snake);

  return (
    <div
      className={legendLivePriceStyles.legendPriceRoot}
      data-testid="last-close-price"
    >
      {isPrimary &&
        iconMapping.map((icon, i) =>
          icon.shouldShow ? (
            <LegendPriceIcons key={i} iconInfo={icon} price={price} />
          ) : null
        )}
      <div
        className={legendLivePriceStyles.priceContainer}
        style={{ color: legendColor }}
      >
        <p>
          {lastClosePrice ? priceFormatter(lastClosePrice) : lastClosePrice}
        </p>
        {snakeMeta.display_format === DisplayFormat.CURRENCY && (
          <p>{snakeMeta.currency.toUpperCase()}</p>
        )}
        {isPriceOrYield && isNonZeroReturn(priceChange.proportionChange) && (
          <>
            <p>{priceChange.change}</p>
            <p>({priceChange.proportionChange})</p>
          </>
        )}
      </div>
    </div>
  );
};
